/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { makeStyles, mergeClasses, Spinner, Text, tokens, Tooltip } from '@fluentui/react-components';
import {
    AddFilled,
    ArrowResetRegular,
    bundleIcon,
    Checkmark20Filled,
    CheckmarkFilled,
    CheckmarkRegular,
    DeleteRegular,
    Dismiss20Filled,
    DismissFilled,
    DismissRegular,
    EditFilled,
    EditRegular,
    Money20Filled,
    MoneyOff20Regular,
    Note20Regular,
    PlayFilled,
    StopFilled,
    TaskListLtrRegular,
} from '@fluentui/react-icons';
import { useAccess } from '../../../../auth/useAccess';
import { Note, Task, TaskStatus, TimeTrackingEntry, UserAccount } from '../../../../data/types';
import { useAccountState } from '../../../../redux/reducers/accountReducer';
import { useUtilities } from '../../../../utilities/useUtilities';
import {
    CustomDataTable,
    CustomDataTableAction,
    CustomDataTableColumn,
    CustomDataTableProps,
    CustomTableCellLayout,
} from '../../../controls/CustomDataTable/CustomDataTable';
import { Column, Row } from '../../../common';
import { ConfirmationDialog } from '../../../dialogs/ConfirmationDialog';
import { CaseNoteService, TimeTrackingService, useActiveTimeEntryService } from '../../../../data/services';
import { AddTimeEntryForTaskDialog } from '../../../dialogs/AddTimeEntryForTaskDialog';
import { AddEditTaskDialog } from '../../../dialogs/AddEditTaskDialog';
import { CaseMilestoneService } from '../../../../data/services/useCaseMilestoneService';
import { CaseTaskNotesDialog } from './CaseTaskNotesDialog';

const useStyles = makeStyles({
    taskText: {
        padding: '0px',
        margin: '0',
        overflow: 'hidden',
        width: 'inherit',
        display: 'block',
        overflowWrap: 'break-word',
        maxWidth: '100%',
    },
    taskTextWordBreak: {
        wordBreak: 'break-all',
    },
    completeBy: {
        color: tokens.colorBrandForeground1,
        pointerEvents: 'none',
        userSelect: 'none',
    },
    completeByOverdue: {
        color: tokens.colorStatusDangerForeground1,
        fontWeight: tokens.fontWeightBold,
    },
});

enum ColumnName {
    task = 'task',
    date = 'date',
    status = 'status',
    billable = 'billable',
    assignedTo = 'assignedTo',
    hours = 'hours',
    milestone = 'milestone',
    notes = 'notes',
}

type CaseTaskTableProps = Partial<CustomDataTableProps<Task>> & {
    tasks: Task[];
    height?: string;
    onSelectionChanged?: (tasks: Task[]) => void;
    onDeleteTasks?: (tasks: Task[]) => Promise<void>;
    timeTrackingService?: TimeTrackingService;
    milestoneService?: CaseMilestoneService;
    noteService?: CaseNoteService;
    showAssigned?: boolean;
    showBillable?: boolean;
    showStatus?: boolean;
    showMilestone?: boolean;
    onSubmit?: (task: Task) => Promise<void>;
    onComplete?: (task: Task) => Promise<void>;
    onClose?: (task: Task) => Promise<void>;
    onReopen?: (task: Task) => Promise<void>;
    selectMilestoneInTask?: boolean;
};
export const CaseTaskTable = (props: CaseTaskTableProps) => {
    const {
        tasks,
        bodyHeight,
        onSelectionChanged,
        timeTrackingService,
        showAssigned = false,
        showBillable = true,
        showStatus = false,
        showMilestone = true,
        milestoneService,
        noteService,
        selectMilestoneInTask = true,
        onSubmit,
        onComplete,
        onClose,
        onReopen,
        ...otherProps
    } = props;

    //Hooks
    const { primaryAccount } = useAccountState();
    const { user, accountUsers } = (primaryAccount as UserAccount) ?? {};
    const access = useAccess(user);
    const utils = useUtilities();
    const { activeTimeEntry } = useActiveTimeEntryService();

    //State
    const [delConfirmOpen, setDelConfirmOpen] = useState<boolean>(false);
    const [editTaskOpen, setEditTaskOpen] = useState<boolean>(false);
    const [addTimeOpen, setAddTimeOpen] = useState<boolean>(false);
    const [notesDialogOpen, setNotesDialogOpen] = useState<boolean>(false);
    const [actionTask, setActionTask] = useState<Task>();
    const [actionTaskNotes, setActionTaskNotes] = useState<Note[]>();

    //Styles
    const styles = useStyles();

    //Logic
    const isActiveTimeEntry = (task: Task) => {
        return !!activeTimeEntry && activeTimeEntry?.taskId == task?.taskId;
    };
    const submitTimeFromTask = async (timeEntry: TimeTrackingEntry) => {
        if (!!timeEntry) {
            await timeTrackingService?.addOrMergeTimeEntryAsync(timeEntry);
        }
    };

    //Table Setup
    const columns: CustomDataTableColumn<Task>[] = [
        {
            id: ColumnName.task,
            header: 'Task',
            renderCell: (task) => {
                const hasLongWord = (text: string | null | undefined, length = 25): boolean => {
                    return !!text && text.split(/\s+/).some((word) => word.length > length);
                };
                const taskTextClasses = mergeClasses(
                    styles.taskText,
                    hasLongWord(task?.title || task?.text) && styles.taskTextWordBreak,
                );

                return (
                    <>
                        <Row gap='medium'>
                            <Column flex='0' vAlign='middle' gap='small'>
                                <TaskListLtrRegular />
                                {/* {!!task?.billable ? (
                                    <Tooltip content='Billable Task' relationship='label'>
                                        <Money20Filled color='green' />
                                    </Tooltip>
                                ) : (
                                    <Tooltip content='Non-billable Task' relationship='label'>
                                        <MoneyOff20Regular color='red' />
                                    </Tooltip>
                                )} */}
                            </Column>
                            <Column flex='3' vAlign='middle' gap='small'>
                                <Text size={300} className={taskTextClasses}>
                                    {task?.title || task?.text}
                                </Text>
                            </Column>
                        </Row>
                    </>
                );
            },
            pointerEvents: true,
            defaultWidth: 350,
            minWidth: 200,
        },
        {
            id: ColumnName.date,
            compare: (a, b) => {
                const aDate = new Date(a.dueBy || 0).valueOf();
                const bDate = new Date(b.dueBy || 0).valueOf();
                return aDate - bDate;
            },
            header: 'Due by',
            renderCell: (task) => {
                const isOverdue = () => {
                    return new Date(task?.dueBy as Date) < new Date();
                };
                const completeByClasses = mergeClasses(styles.completeBy, isOverdue() && styles.completeByOverdue);
                const epoch = new Date('1970-01-01T00:00:00Z');
                const hasCompleteBy = () => {
                    const compDate = new Date(task?.dueBy as Date);
                    return !!task?.dueBy && compDate > epoch;
                };
                const getCompleteBy = () => {
                    if (!!hasCompleteBy()) {
                        const compByDate = new Date(`${task?.dueBy as Date}`);
                        return `${utils.date.formatDate(compByDate, { month: 'short', day: 'numeric', year: 'numeric', weekday: 'short' }, true)}`;
                    }
                };
                return (
                    <>
                        {hasCompleteBy() && (
                            <Column align='center' noGap>
                                <Text weight='semibold' className={completeByClasses}>
                                    {getCompleteBy()}
                                </Text>
                                <>
                                    {isOverdue() && (
                                        <Text size={200} className={completeByClasses}>
                                            {isOverdue() ? 'Overdue' : ''}
                                        </Text>
                                    )}
                                </>
                            </Column>
                        )}
                    </>
                );
            },
            defaultWidth: 150,
            minWidth: 100,
        },
        {
            id: ColumnName.status,
            compare: (a, b) => {
                return a.status?.localeCompare(b.status || '') || 0;
            },
            header: 'Status',
            renderCell: (task) => {
                return (
                    <>
                        <Row gap='small'>
                            <Column width='auto' vAlign='middle'>
                                {task.status == TaskStatus.completed && <Checkmark20Filled color='green' />}
                                {task.status == TaskStatus.closed && <Dismiss20Filled color='red' />}
                            </Column>
                            <Column>
                                <Text>{task.status}</Text>
                            </Column>
                        </Row>
                    </>
                );
            },
            show: showStatus,
            defaultWidth: 150,
            minWidth: 100,
        },
        {
            id: ColumnName.billable,
            compare: (a, b) => {
                return Number(a.billable) - Number(b.billable);
            },
            header: 'Billable?',
            renderCell: (task) => {
                return (
                    <>
                        <Row noGap>
                            <Column width='auto'>
                                {task?.billable && <Money20Filled color='green' />}
                                {!task?.billable && <MoneyOff20Regular color='red' />}
                            </Column>
                            <Column align='center' padding='pl-1'>
                                <Text weight='semibold'>{task?.billable ? 'Billable' : 'Non-Billable'}</Text>
                            </Column>
                        </Row>
                    </>
                );
            },
            show: !!showBillable,
            defaultWidth: 125,
            minWidth: 100,
        },
        {
            id: ColumnName.milestone,
            compare: (a, b) => {
                const msA = milestoneService?.milestones.find((x) => x.milestoneId == a.milestoneId);
                const msB = milestoneService?.milestones.find((x) => x.milestoneId == b.milestoneId);
                return (msA?.sortKey || 0) - (msB?.sortKey || 0);
            },
            header: 'Milestone',
            renderCell: (task) => {
                const ms = milestoneService?.milestones.find((x) => x.milestoneId == task.milestoneId);
                return <>{!!ms && <Text>{ms?.name}</Text>}</>;
            },
            show: showMilestone,
            defaultWidth: 150,
            minWidth: 100,
        },
        {
            id: ColumnName.assignedTo,
            compare: (a, b) => {
                const aName = utils.getUsersName(a?.username as string, accountUsers || []);
                const bName = utils.getUsersName(b?.username as string, accountUsers || []);
                return aName?.localeCompare(bName || '') || 0;
            },
            header: 'Assigned To',
            renderCell: (task) => {
                const name = utils.getUsersName(task?.userId as string, accountUsers || []);
                return (
                    <>
                        <Text>{name}</Text>
                    </>
                );
            },
            show: showAssigned,
            defaultWidth: 150,
            minWidth: 100,
        },
        {
            id: ColumnName.notes,
            header: 'Notes',
            renderCell: (task) => {
                const taskNotes = noteService?.notes.filter((note) => note.taskId == task.taskId) || [];
                const notesCount = taskNotes.length;
                return (
                    <>
                        {!!notesCount && (
                            <Tooltip content={`${notesCount} ${notesCount === 1 ? 'Note' : 'Notes'}`} relationship='label'>
                                <span
                                    onClick={() => {
                                        setActionTask(task);
                                        setActionTaskNotes(taskNotes);
                                        setNotesDialogOpen(true);
                                    }}
                                >
                                    <Note20Regular />
                                </span>
                            </Tooltip>
                        )}
                    </>
                );
            },
            justifyContent: 'center',
            defaultWidth: 50,
            minWidth: 50,
            pointerEvents: true,
        },
    ];

    //Icons
    const EditBundle = bundleIcon(EditFilled, EditRegular);
    const CompleteBundle = bundleIcon(CheckmarkFilled, CheckmarkRegular);
    const CloseBundle = bundleIcon(DismissFilled, DismissRegular);

    const actions: CustomDataTableAction<Task>[] = [
        {
            id: 'startTime',
            ariaLabel: 'Start Timer',
            canAccess: (task) =>
                task?.status == TaskStatus.open &&
                task?.userId == user.userId &&
                (access.timeTracking.canAddEditTrackedTime(task?.caseId) || false),
            icon: (task: Task) => {
                return isActiveTimeEntry(task) ? (
                    <Spinner size='extra-tiny' />
                ) : (
                    <PlayFilled
                        color={
                            !!activeTimeEntry ? tokens.colorNeutralForegroundDisabled : tokens.colorStatusSuccessForeground1
                        }
                    />
                );
            },
            label: 'Start Timer',
            tooltip: 'Start Timer',
            noHideOnHover: (task: Task) => isActiveTimeEntry(task),
            onClick: (task) => {
                if (!!task && !!timeTrackingService && !activeTimeEntry) {
                    void timeTrackingService.startTimerAsync(task);
                }
            },
            onMenuClick: (task) => {
                if (!!task && !!timeTrackingService && !activeTimeEntry) {
                    void timeTrackingService.startTimerAsync(task);
                }
            },
        },
        {
            id: 'stopTime',
            ariaLabel: 'Stop Timer',
            canAccess: (task) =>
                task?.status == TaskStatus.open &&
                task?.userId == user.userId &&
                (access.timeTracking.canAddEditTrackedTime(task?.caseId) || false),
            icon: (task: Task) => {
                return (
                    <StopFilled
                        color={
                            !isActiveTimeEntry(task)
                                ? tokens.colorNeutralForegroundDisabled
                                : tokens.colorStatusDangerForeground1
                        }
                    />
                );
            },
            label: 'Stop Timer',
            tooltip: 'Stop Timer',
            noHideOnHover: (task: Task) => isActiveTimeEntry(task),
            onClick: (task) => {
                if (!!task && !!timeTrackingService) {
                    void timeTrackingService.stopTimerAsync(task);
                }
            },
            onMenuClick: (task) => {
                if (!!task && !!timeTrackingService) {
                    void timeTrackingService.stopTimerAsync(task);
                }
            },
        },
        {
            id: 'addTime',
            ariaLabel: 'Add Time for Task',
            canAccess: (task) =>
                task?.status == TaskStatus.open &&
                task?.userId == user.userId &&
                (access.timeTracking.canAddEditTrackedTime(task?.caseId) || false),
            icon: <AddFilled />,
            label: 'Add Time',
            tooltip: 'Add Time',
            //noHideOnHover: (task: Task) => isActiveTimeEntry(task),
            onClick: (task) => {
                setActionTask(task);
                setAddTimeOpen(true);
            },
            onMenuClick: (task) => {
                setActionTask(task);
                setAddTimeOpen(true);
            },
            renderDialog: (task, children) => {
                return (
                    <AddTimeEntryForTaskDialog caseId={task.caseId} task={task} onSubmit={submitTimeFromTask}>
                        {children}
                    </AddTimeEntryForTaskDialog>
                );
            },
        },
        {
            id: '',
            label: '',
            divider: true,
            canAccess: (task) => task?.status == TaskStatus.open,
        },
        {
            id: 'edit',
            ariaLabel: 'Edit Task',
            canAccess: (task) => task?.status == TaskStatus.open && (access.cases.canEditTasks(task?.caseId) || false),
            icon: <EditBundle />,
            label: 'Edit Task',
            tooltip: 'Edit Task',
            onMenuClick: (task) => {
                setActionTask(task);
                setEditTaskOpen(true);
            },
            renderDialog: (task, children) => {
                return (
                    <AddEditTaskDialog
                        onSubmit={onSubmit}
                        initialCaseId={task.caseId}
                        taskToEdit={task}
                        canChangeCase={false}
                        selectMilestone={selectMilestoneInTask}
                    >
                        {children!}
                    </AddEditTaskDialog>
                );
            },
        },
        {
            id: 'complete',
            ariaLabel: 'Complete Task',
            canAccess: (task) => task?.status == TaskStatus.open && task?.userId == user.userId,
            icon: <CompleteBundle />,
            label: 'Complete Task',
            tooltip: 'Complete Task',
            singleSelectItemOnClick: true,
            onClick: (task) => {
                if (!!task) {
                    onComplete?.(task);
                }
            },
            onMenuClick: (task) => {
                if (!!task) {
                    onComplete?.(task);
                }
            },
        },
        {
            id: 'close',
            ariaLabel: 'Close Task',
            canAccess: (task) =>
                task?.status == TaskStatus.open &&
                (task?.userId == user.userId || access.cases.canEditCaseAsAdmin(task?.caseId) || false),
            icon: <CloseBundle />,
            label: 'Close Task',
            tooltip: 'Close Task',
            singleSelectItemOnClick: true,
            onClick: (task) => {
                if (!!task) {
                    onClose?.(task);
                }
            },
            onMenuClick: (task) => {
                if (!!task) {
                    onClose?.(task);
                }
            },
        },
        {
            id: 'reopen',
            ariaLabel: 'Reopen Task',
            canAccess: (task) =>
                (task?.status == TaskStatus.completed || task?.status == TaskStatus.closed) && task?.userId == user.userId,
            icon: <ArrowResetRegular />,
            label: 'Reopen Task',
            tooltip: 'Reopen Task',
            singleSelectItemOnClick: true,
            onClick: (task) => {
                if (!!task) {
                    onReopen?.(task);
                }
            },
            onMenuClick: (task) => {
                if (!!task) {
                    onReopen?.(task);
                }
            },
        },
    ];

    return (
        <>
            <CustomDataTable<Task>
                items={tasks || []}
                rowId='taskId'
                bodyHeight={bodyHeight}
                onSelectionChanged={onSelectionChanged as any}
                columns={columns}
                actions={actions}
                onDoubleClickItem={(item: Task) => {
                    if (item.status == TaskStatus.open) {
                        setActionTask(item);
                        setEditTaskOpen(true);
                    }
                }}
                {...otherProps}
            />
            <ConfirmationDialog
                title='Confirm Delete'
                icon={<DeleteRegular />}
                text={`Are you sure you want to delete this task?`}
                open={delConfirmOpen}
                onOpenChange={(isOpen) => {
                    setDelConfirmOpen(isOpen as boolean);
                    setActionTask(undefined);
                }}
                onConfirm={() => {}}
            />
            <AddEditTaskDialog
                onSubmit={onSubmit}
                initialCaseId={actionTask?.caseId}
                taskToEdit={actionTask}
                selectMilestone={selectMilestoneInTask}
                open={editTaskOpen}
                onOpenChange={(isOpen) => {
                    setEditTaskOpen(isOpen as boolean);
                    //if (!isOpen) setActionTask(undefined);
                }}
            />
            <AddTimeEntryForTaskDialog
                open={addTimeOpen}
                onOpenChange={(isOpen) => {
                    setAddTimeOpen(isOpen as boolean);
                }}
                caseId={actionTask?.caseId}
                task={actionTask}
                onSubmit={submitTimeFromTask}
            />
            <CaseTaskNotesDialog
                title={actionTask?.title || actionTask?.text || 'Task Notes'}
                notes={actionTaskNotes}
                open={notesDialogOpen}
                onOpenChange={(isOpen) => {
                    setNotesDialogOpen(isOpen as boolean);
                    if (!isOpen) {
                        // setActionTask(undefined);
                        // setActionTaskNotes([]);
                    }
                }}
            />
        </>
    );
};
